import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

/**
 * Server-backed "NEW feature" dismissal registry.
 *
 * Dismissals are persisted in `user_feature_dismissals` on the backend
 * (migration 046). We load them once when the auth user changes, keep
 * a local Set in memory for O(1) lookups, and POST on dismiss. Browsers
 * clearing storage don't lose state because the server is authoritative.
 *
 * Usage:
 *   const { isDismissed, dismiss } = useNewFeatures();
 *   if (!isDismissed('builder.structured-mode')) ...
 *
 * Or simpler — use the <NewBadge featureId="..." /> component which
 * handles visibility and the dismiss click itself.
 */

interface NewFeaturesContextValue {
  loading: boolean;
  isDismissed: (featureId: string) => boolean;
  dismiss: (featureId: string) => Promise<void>;
}

const NewFeaturesContext = createContext<NewFeaturesContextValue>({
  loading: false,
  isDismissed: () => false,
  dismiss: async () => {},
});

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = (): HeadersInit => ({
  'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

export const NewFeaturesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);

  // Load the authoritative set from the server on mount. If the user
  // isn't logged in yet, we simply start empty — the provider mounts
  // high enough that re-login will remount and refetch.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }
        const res = await fetch('/api/user-preferences/dismissed-features', {
          headers: authHeaders(),
        });
        if (!res.ok) {
          setLoading(false);
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        const ids: string[] = Array.isArray(json?.data) ? json.data : [];
        setDismissedIds(new Set(ids));
      } catch {
        // Network or auth failure — fail open (badges still show).
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const isDismissed = useCallback((featureId: string) => dismissedIds.has(featureId), [dismissedIds]);

  const dismiss = useCallback(async (featureId: string) => {
    // Optimistic: update UI immediately, roll back on failure.
    setDismissedIds(prev => {
      if (prev.has(featureId)) return prev;
      const next = new Set(prev);
      next.add(featureId);
      return next;
    });
    try {
      const res = await fetch('/api/user-preferences/dismissed-features', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ feature_id: featureId }),
      });
      if (!res.ok) throw new Error('dismiss failed');
    } catch {
      // Roll back if the server rejected the write
      setDismissedIds(prev => {
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
    }
  }, []);

  return (
    <NewFeaturesContext.Provider value={{ loading, isDismissed, dismiss }}>
      {children}
    </NewFeaturesContext.Provider>
  );
};

export const useNewFeatures = () => useContext(NewFeaturesContext);

/**
 * Drop-in "NEW" pill badge for any UI surface.
 * Renders nothing once dismissed.
 *
 * @example
 *   <NavItem>
 *     Emissions Dashboard
 *     <NewBadge featureId="nav.emissions-dashboard" />
 *   </NavItem>
 */
export const NewBadge: React.FC<{
  featureId: string;
  label?: string;
  title?: string;
  className?: string;
}> = ({ featureId, label = 'NEW', title, className = '' }) => {
  const { isDismissed, dismiss, loading } = useNewFeatures();
  if (loading) return null;
  if (isDismissed(featureId)) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 ml-1.5 text-[9px] font-bold uppercase tracking-widest rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white pl-2 pr-1 py-0.5 shadow-sm ${className}`}
      title={title || 'Click × to dismiss'}
    >
      {label}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          dismiss(featureId);
        }}
        className="w-3.5 h-3.5 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
        aria-label="Dismiss new feature indicator"
      >
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
};
